import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "components/fields/InputField";
import { adminAuthService } from "services/adminAuthService";
import { authStorage } from "services/authStorage";
import { useAppLanguage } from "hooks/useAppLanguage";
import { getAuthPortalCopy } from "lib/authPortalCopy";

export default function AdminSignIn() {
  const navigate = useNavigate();
  const language = useAppLanguage();
  const t = getAuthPortalCopy("admin", language);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authStorage.isAuthenticated("admin")) {
      navigate(authStorage.dashboardPath("admin"), { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError(
        language === "ar"
          ? "يرجى إدخال البريد الإلكتروني وكلمة المرور."
          : "Please enter your email and password."
      );
      return;
    }

    setLoading(true);
    try {
      await adminAuthService.login({ email, password });
      navigate(authStorage.dashboardPath("admin"));
    } catch (err: any) {
      const data = err.response?.data;
      const message =
        (typeof data?.message === "string" && data.message) ||
        data?.errors?.email?.[0] ||
        (language === "ar"
          ? "بيانات الدخول غير صحيحة. حاول مرة أخرى."
          : "Invalid credentials. Please try again.");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:p-8">
        <div className="mb-6">
          <span className="inline-flex rounded-full bg-brand-500/10 px-3 py-1 text-xs font-semibold text-brand-600 dark:text-brand-300">
            {t.portalLabel}
          </span>
          <h1 className="mt-3 text-2xl font-bold text-navy-800 dark:text-white sm:text-3xl">
            {t.title}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {t.subtitle}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-200"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-1">
          <InputField
            variant="auth"
            extra="mb-4"
            label={`${t.emailLabel} *`}
            placeholder={t.emailPlaceholder}
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <InputField
            variant="auth"
            extra="mb-4"
            label={`${t.passwordLabel} *`}
            placeholder={t.passwordPlaceholder}
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            type="submit"
            disabled={loading}
            className="mt-2 w-full rounded-xl bg-brand-500 py-3 text-base font-semibold text-white transition hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            {loading ? t.submitAdminLoading : t.submitAdmin}
          </button>
        </form>

        <p className="mt-6 border-t border-gray-100 pt-5 text-center text-xs text-gray-500 dark:border-white/10 dark:text-gray-400">
          {language === "ar" ? "هذه بوابة الإدارة فقط" : "Admin portal only"}
        </p>
      </div>
    </div>
  );
}
